import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Popconfirm,
  Skeleton,
  Space,
  Switch,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import {
  EditOutlined,
  InboxOutlined,
  LinkOutlined,
  PlusOutlined,
  RollbackOutlined,
  SendOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import type { StudioMemberRow, StudioTeamRow } from '../contract';
import {
  StudioViewer,
  archiveStudioTeam,
  getStudioMembers,
  getStudioTeams,
  removeTeamMember,
  restoreStudioTeam,
  studioAbilities,
  useStudioLoad,
} from '../api';
import { mapStudioError } from '../studio-error';
import {
  CHANNEL_OUTSIDE_ADMIN_HINT,
  CHANNEL_OUTSIDE_TEXT,
  EMPTY_TEXT,
  KC_MODE_LABEL,
  NO_KC_THIS_MONTH,
  PENDING_ADMIN_TAG,
  REASON_MIN,
  TEAM_KIND_LABEL,
  TEAM_ROLE_LABEL,
  unassignedLine,
} from '../labels';
import { asOfLabel, displayName, formatKc } from '../format';
import MemberCell from '../components/member-cell';
import AssignTeamModal, { AssignMember } from '../components/assign-team-modal';
import TeamFormModal, { TeamFormMemberOption } from '../components/team-form-modal';
import TeamChannelModal from '../components/team-channel-modal';
import { StudioLoadError } from './studio-header';
import styles from '../styles.module.scss';

export interface TeamsTabProps {
  studioId: number;
  viewer: StudioViewer;
  /** Tháng KC của thẻ nhóm (tham số `month` trên URL); không có = tháng UTC hiện tại (S14 mặc định). */
  month?: string;
  /** Sau mỗi thao tác (tải lại số của trang). */
  onChanged?: () => void;
}

interface ChannelTarget {
  team: StudioTeamRow;
  mode: 'admin' | 'request';
  action: 'set' | 'clear';
}

/** S10 trả tối đa 100 dòng mỗi trang: đọc hết (tối đa 20 trang) để chọn người cho Nhóm nhảy. */
const MEMBER_PAGE_LIMIT = 100;
const MEMBER_MAX_PAGES = 20;

async function loadAllMembers(studioId: number): Promise<StudioMemberRow[]> {
  const out: StudioMemberRow[] = [];
  for (let page = 1; page <= MEMBER_MAX_PAGES; page += 1) {
    const res = await getStudioMembers(studioId, { page, limit: MEMBER_PAGE_LIMIT });
    out.push(...(res?.data || []));
    if (!res?.metadata || page >= res.metadata.totalPage) break;
  }
  return out;
}

const memberName = (m: StudioMemberRow) =>
  displayName({ nickName: m.nickName, username: m.username, uid: m.uid });

/**
 * Tab Nhóm nhảy của Chi tiết Studio (SPEC Studio mục 8.7.6): lưới thẻ S14. Admin và Quản lý của chính Studio
 * tạo / sửa / lưu trữ nhóm và xếp người (không kéo-thả ở giai đoạn 1). UID kênh CHỈ Admin gắn / đổi / bỏ
 * (S27); Quản lý gửi đề nghị gắn kênh (S24 loại TEAM_CHANNEL). SMOD chỉ xem.
 */
const TeamsTab = ({ studioId, viewer, month, onChanged }: TeamsTabProps) => {
  const abilities = studioAbilities(viewer.mine, studioId);
  const canManage = abilities.manageTeams;
  const isAdmin = abilities.admin;
  const canRequest = abilities.request;

  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StudioTeamRow | null>(null);
  const [adding, setAdding] = useState<StudioTeamRow | null>(null);
  const [channel, setChannel] = useState<ChannelTarget | null>(null);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const teams = useStudioLoad(
    () => getStudioTeams(studioId, { status: showArchived ? 'ALL' : 'ACTIVE', month }),
    [studioId, showArchived, month],
    { resetKey: studioId }
  );
  // Danh sách chọn người: chỉ tải khi mở modal tạo nhóm / thêm người, rồi dùng lại cho lần mở sau (mỗi trang
  // S10 tính KC cả Studio nên không đọc lại mỗi lần mở). Thao tác trong tab tăng `poolVersion` → bản đã tải
  // thành cũ (cột nhóm hiện tại của từng người đổi) → lần mở sau tải lại.
  const needMembers = creating || !!adding;
  const [poolVersion, setPoolVersion] = useState(0);
  const [poolFresh, setPoolFresh] = useState<string | null>(null);
  const poolKey = `${studioId}:${poolVersion}`;
  const members = useStudioLoad(
    () => loadAllMembers(studioId).then((rows) => ({ poolKey, rows })),
    [poolKey],
    { enabled: needMembers && poolFresh !== poolKey, resetKey: studioId }
  );
  useEffect(() => {
    if (members.data) setPoolFresh(members.data.poolKey);
  }, [members.data]);
  const pool = members.data?.rows;

  // Lỗi tải danh sách người hiện khi modal đang mở (không chặn tạo nhóm rỗng).
  useEffect(() => {
    if (members.error) notification.error({ message: mapStudioError(members.error) });
  }, [members.error]);

  const list = teams.data?.data || [];
  const activeTeams = list.filter((t) => t.status === 'ACTIVE');

  const formOptions: TeamFormMemberOption[] = useMemo(
    () =>
      (pool || []).map((m) => ({
        key: m.key,
        name: memberName(m),
        userId: m.userId,
        team: m.team ? { id: m.team.id, name: m.team.name } : null,
      })),
    [pool]
  );
  const assignOptions: AssignMember[] = useMemo(
    () =>
      (pool || []).map((m) => ({
        key: m.key,
        name: memberName(m),
        team: m.team ? { id: m.team.id, name: m.team.name } : null,
      })),
    [pool]
  );

  const afterChange = () => {
    teams.reload();
    setPoolVersion((v) => v + 1);
    onChanged?.();
  };

  const runBusy = async (key: string, work: () => Promise<unknown>, done: string) => {
    setBusyKey(key);
    try {
      await work();
      notification.success({ message: done });
      afterChange();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setBusyKey(null);
    }
  };

  const openArchive = (t: StudioTeamRow) =>
    setAction({
      title: `Lưu trữ ${t.name}`,
      description: (
        <span>
          Mọi thành viên rời nhóm từ hôm nay
          {t.channelTiktokUid ? '; UID kênh thôi là kênh của nhóm từ hôm nay' : ''}. Nhóm đã lưu trữ
          vẫn xem được khi bật "Hiện nhóm đã lưu trữ".
        </span>
      ),
      noteRequired: true,
      noteLabel: `Lý do (ít nhất ${REASON_MIN} ký tự)`,
      confirmText: 'Lưu trữ',
      danger: true,
      onSubmit: async (note) => {
        if ((note || '').length < REASON_MIN) {
          notification.error({ message: `Lý do cần ít nhất ${REASON_MIN} ký tự.` });
          throw new Error('REASON_TOO_SHORT');
        }
        try {
          await archiveStudioTeam(studioId, t.id, note as string);
          notification.success({ message: `Đã lưu trữ ${t.name}.` });
          afterChange();
        } catch (err) {
          notification.error({ message: mapStudioError(err) });
          throw err;
        }
      },
    });

  const renderKc = (t: StudioTeamRow): ReactNode => {
    if (t.channelOutsideStudio) {
      return (
        <span>
          <Tag color="red">{CHANNEL_OUTSIDE_TEXT}</Tag>
          {isAdmin && <span className={styles.muted}>{CHANNEL_OUTSIDE_ADMIN_HINT}</span>}
        </span>
      );
    }
    if (!t.kc || t.kc.diamonds === null) {
      return (
        <span>
          — <span className={styles.muted}>{NO_KC_THIS_MONTH}</span>
        </span>
      );
    }
    const asOf = asOfLabel(t.kc.asOf);
    return (
      <span>
        <strong>{formatKc(t.kc.diamonds)}</strong>
        {asOf && <span className={styles.muted}> {asOf}</span>}
      </span>
    );
  };

  const channelButtons = (t: StudioTeamRow): ReactNode[] => {
    const out: ReactNode[] = [];
    const active = t.status === 'ACTIVE';
    if (isAdmin) {
      if (active) {
        out.push(
          <Button
            key="set"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => setChannel({ team: t, mode: 'admin', action: 'set' })}
          >
            {t.channelTiktokUid ? 'Đổi kênh' : 'Gắn kênh'}
          </Button>
        );
      }
      if (t.channelTiktokUid) {
        out.push(
          <Button
            key="clear"
            size="small"
            danger
            onClick={() => setChannel({ team: t, mode: 'admin', action: 'clear' })}
          >
            Bỏ kênh
          </Button>
        );
      }
      return out;
    }
    if (canRequest && active) {
      const pending = !!t.pendingChannelRequest;
      out.push(
        <Tooltip key="request" title={pending ? PENDING_ADMIN_TAG : undefined}>
          <Button
            size="small"
            icon={<SendOutlined />}
            disabled={pending}
            onClick={() => setChannel({ team: t, mode: 'request', action: 'set' })}
          >
            Đề nghị gắn kênh
          </Button>
        </Tooltip>
      );
      if (t.channelTiktokUid && !pending) {
        out.push(
          <Button
            key="request-clear"
            size="small"
            onClick={() => setChannel({ team: t, mode: 'request', action: 'clear' })}
          >
            Đề nghị bỏ kênh
          </Button>
        );
      }
    }
    return out;
  };

  const teamButtons = (t: StudioTeamRow): ReactNode[] => {
    if (!canManage) return [];
    if (t.status === 'ARCHIVED') {
      return [
        <Popconfirm
          key="restore"
          title={`Mở lại ${t.name}?`}
          description="Thành viên cũ không tự quay lại nhóm."
          okText="Mở lại"
          cancelText="Huỷ"
          onConfirm={() =>
            runBusy(
              `restore:${t.id}`,
              () => restoreStudioTeam(studioId, t.id),
              `Đã mở lại ${t.name}.`
            )
          }
        >
          <Button size="small" icon={<RollbackOutlined />} loading={busyKey === `restore:${t.id}`}>
            Mở lại
          </Button>
        </Popconfirm>,
      ];
    }
    return [
      <Button key="add" size="small" icon={<UserAddOutlined />} onClick={() => setAdding(t)}>
        Thêm người
      </Button>,
      <Button key="edit" size="small" icon={<EditOutlined />} onClick={() => setEditing(t)}>
        Sửa
      </Button>,
      <Button key="archive" size="small" icon={<InboxOutlined />} onClick={() => openArchive(t)}>
        Lưu trữ
      </Button>,
    ];
  };

  const renderTeam = (t: StudioTeamRow) => {
    const active = t.status === 'ACTIVE';
    const buttons = [...teamButtons(t), ...channelButtons(t)];
    return (
      <Card
        key={t.id}
        size="small"
        title={
          <span className={styles.tags}>
            <span className={styles.cellTitle}>{t.name}</span>
            <Tag color={t.kcMode === 'CHANNEL' ? 'purple' : 'default'}>
              {KC_MODE_LABEL[t.kcMode]}
            </Tag>
            {t.kind !== 'DANCE' && <Tag>{TEAM_KIND_LABEL[t.kind] || t.kind}</Tag>}
            {!active && <Tag>Đã lưu trữ</Tag>}
          </span>
        }
      >
        <Descriptions
          size="small"
          column={1}
          items={[
            {
              key: 'leader',
              label: TEAM_ROLE_LABEL.LEADER,
              children: t.leader ? displayName(t.leader) : '—',
            },
            { key: 'count', label: 'Số người', children: formatKc(t.memberCount) },
            {
              key: 'channel',
              label: 'UID kênh',
              children: (
                <span className={styles.tags}>
                  <span style={{ overflowWrap: 'anywhere' }}>{t.channelTiktokUid || '—'}</span>
                  {t.pendingChannelRequest && <Tag color="gold">{PENDING_ADMIN_TAG}</Tag>}
                </span>
              ),
            },
            { key: 'kc', label: 'KC tháng', children: renderKc(t) },
          ]}
        />
        {t.members.length > 0 && (
          <ul className={styles.teamMembers} style={{ marginTop: 8 }}>
            {t.members.map((m) => (
              <li key={m.id} className={styles.teamMember}>
                <MemberCell
                  name={m.name}
                  uid={m.uid}
                  avatar={m.avatar}
                  size={28}
                  sub={TEAM_ROLE_LABEL[m.role] || m.role}
                />
                {canManage && active && (
                  <Popconfirm
                    title={`Cho ${m.name} rời ${t.name}?`}
                    okText="Rời nhóm"
                    cancelText="Huỷ"
                    onConfirm={() =>
                      runBusy(
                        `leave:${m.id}`,
                        () => removeTeamMember(studioId, t.id, m.id),
                        `${m.name} đã rời ${t.name}.`
                      )
                    }
                  >
                    <Button size="small" type="link" danger loading={busyKey === `leave:${m.id}`}>
                      Rời nhóm
                    </Button>
                  </Popconfirm>
                )}
              </li>
            ))}
          </ul>
        )}
        {buttons.length > 0 && (
          <Space size={4} wrap style={{ marginTop: 12 }}>
            {buttons}
          </Space>
        )}
      </Card>
    );
  };

  const firstLoad = teams.loading && !teams.data;

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          {teams.data && (
            <span className={styles.summaryLine}>{unassignedLine(teams.data.unassigned)}</span>
          )}
          <Space size={6}>
            <Switch
              checked={showArchived}
              onChange={setShowArchived}
              aria-label="Hiện nhóm đã lưu trữ"
            />
            <span>Hiện nhóm đã lưu trữ</span>
          </Space>
        </div>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            Tạo Nhóm nhảy
          </Button>
        )}
      </div>

      {teams.error && <StudioLoadError error={teams.error} onRetry={teams.reload} />}

      {firstLoad ? (
        <Skeleton active />
      ) : list.length ? (
        <div className={styles.teamGrid}>{list.map(renderTeam)}</div>
      ) : (
        !teams.error && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT.teams} />
        )
      )}

      <TeamFormModal
        open={creating || !!editing}
        studioId={studioId}
        team={editing}
        memberOptions={formOptions}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onDone={afterChange}
      />
      <AssignTeamModal
        open={!!adding}
        studioId={studioId}
        team={adding}
        teams={activeTeams}
        memberOptions={assignOptions}
        onClose={() => setAdding(null)}
        onDone={afterChange}
      />
      <TeamChannelModal
        open={!!channel}
        studioId={studioId}
        team={channel?.team ?? null}
        mode={channel?.mode ?? 'admin'}
        action={channel?.action}
        onClose={() => setChannel(null)}
        onDone={afterChange}
      />
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default TeamsTab;
